/**
 * Regulatory-library selector + key-MCL reference card.
 *
 * Shown at the top of the polygon site page. Changing the selection cascades
 * through:
 *   - Plume polygons (boundaries refetched with library_slug)
 *   - Contaminants vs MCL legend
 *   - Sentinel/DW recommendations
 *   - Cost Analysis "new well installation" (via DW count)
 *
 * The MCL card shows BTEX + TPH limits for the chosen library, with a small
 * tag indicating any per-compound EPA-MCL fallback (when the chosen library
 * doesn't define a particular compound).
 */
import { useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { polygonApi } from '../../api/polygon';
import type { RegLibrary, RegLibraryKeyMcl } from '../../api/polygon';

interface RegLibrarySelectorProps {
  value: string;
  onChange: (slug: string) => void;
}

export function RegLibrarySelector({ value, onChange }: RegLibrarySelectorProps) {
  const [libraries, setLibraries] = useState<RegLibrary[]>([]);
  const [keyMcls, setKeyMcls] = useState<RegLibraryKeyMcl[]>([]);
  const [librariesLoading, setLibrariesLoading] = useState(true);
  const [mclsLoading, setMclsLoading] = useState(false);

  // Fetch the available libraries once.
  useEffect(() => {
    let cancelled = false;
    polygonApi.listRegLibraries()
      .then(resp => {
        if (cancelled) return;
        setLibraries(resp.libraries);
        // If parent hasn't picked anything yet, default to system fallback.
        if (!value && resp.default_slug) onChange(resp.default_slug);
        setLibrariesLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLibrariesLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch key MCLs whenever the chosen library changes.
  useEffect(() => {
    if (!value) return;
    let cancelled = false;
    setMclsLoading(true);
    polygonApi.getRegLibraryKeyMcls(value)
      .then(resp => {
        if (cancelled) return;
        setKeyMcls(resp.compounds);
        setMclsLoading(false);
      })
      .catch(() => {
        if (!cancelled) setMclsLoading(false);
      });
    return () => { cancelled = true; };
  }, [value]);

  return (
    <div className="flex flex-col h-full gap-2">{/* h-full so col 4 stretches to the tallest grid column (Contaminant Stats) and bottom-aligns with it */}
      {/* Dropdown card */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] text-mono-500 uppercase tracking-wider">Reg Library</span>
          {librariesLoading && <ArrowPathIcon className="w-3 h-3 text-mono-500 animate-spin" />}
        </div>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={librariesLoading || libraries.length === 0}
          className="w-full px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
        >
          {libraries.map(lib => (
            <option key={lib.slug} value={lib.slug}>
              {lib.display_name} ({lib.authority})
            </option>
          ))}
        </select>
      </div>

      {/* Key-MCL card — stacked rows so the column reads top-to-bottom.
          flex-1 lets this card fill remaining height so the column bottom
          aligns with the other dashboard columns. */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3 flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] text-mono-500 uppercase tracking-wider">Key MCLs (mg/L)</span>
          {mclsLoading && <ArrowPathIcon className="w-3 h-3 text-mono-500 animate-spin" />}
        </div>
        {keyMcls.length === 0 && !mclsLoading ? (
          <div className="text-[10px] text-mono-600">No data</div>
        ) : (
          <div className="space-y-1">
            {keyMcls.map(c => (
              <div
                key={c.analyte_code}
                className={`flex items-center justify-between text-[10px] px-1.5 py-0.5 rounded ${
                  c.mcl_mgl == null
                    ? 'text-mono-600'
                    : c.fallback_to_epa_mcl
                    ? 'text-amber-300'
                    : 'text-cyan-300'
                }`}
                title={
                  c.mcl_mgl == null
                    ? 'No limit defined in any library — plume builder will skip this compound.'
                    : c.fallback_to_epa_mcl
                    ? 'Selected library has no entry — falling back to EPA-MCL for this compound.'
                    : 'Defined directly in the selected library.'
                }
              >
                <span className="text-mono-500 truncate">{c.display}</span>
                <span className="flex items-center gap-1">
                  <span className={c.mcl_mgl == null ? 'italic' : 'font-semibold'}>
                    {c.mcl_mgl == null ? '—' : c.mcl_mgl}
                  </span>
                  {c.fallback_to_epa_mcl && (
                    <span className="text-[8px] uppercase tracking-wider opacity-70">epa</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
